<template>
	<ViewLayout>
		<template #header-title>
			Languages
		</template>
		<template #header-after>
			<Tabs
				:tab.sync="tab"
				:tabs="tabs"
			/>
		</template>
		<template #content>
			<TabItems
				:tab="tab"
				:tabs="tabs"
			>
				<template #0>
					<LanguagesTab
						:table-records="assignedLanguages"
						:user-full-permissions="userFullPermissions"
						@load-languages="loadLanguages"
					/>
				</template>
				<template #1>
					<OptionLabelTranslationsTab
						:table-headers="optionLabelTranslationsTableHeaders"
						:option-label-translations="filteredOptionLabelTranslations"
						:multiple-languages-enabled="multipleLanguagesEnabled"
						:user-full-permissions="userFullPermissions"
						@load-option-labels="loadOptionLables"
					/>
				</template>
			</TabItems>
		</Template>
	</ViewLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import Tabs from '../../../../../../shared/components/tabs.vue'
import TabItems from '../../../../../../shared/components/tab-items.vue'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { LANGUAGES_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { getAssignedLanguages, getLanguages, createLanguage, updateLanguage, updateDefaultLanguageName, getLanguageTabTranslations } from '../../../../../../shared/utils/api/languages.js'
import OptionLabelTranslationsTab from './option-label-translations-tab.vue'
import LanguagesTab from './languages-tab.vue'

export default {
	components: {
		Tabs,
		TabItems,
		ViewLayout,
		OptionLabelTranslationsTab,
		LanguagesTab
	},
	setup () {
		return {
			showSnackbar
		}
	},
	data () {
		return {
			searchQuery: null,
			languageCodeSearchQuery: null,
			showAddModal: false,
			assignedLanguage: null,
			languages: [],
			assignedLanguages: [],
			defaultLanguage: '',
			selectedLanguageName: '',
			isEdit: false,
			tabs: [{ title: 'Languages', slot: '0' }, { title: 'Language Translations', slot: '1' }],
			tab: '0',
			optionLabelTranslations: [],
			lookupData: []
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		calculateSuggestions () {
			return this.languages.find(({ languageLookupId }) => languageLookupId === this.assignedLanguage.languageLookupId)
				?.googleLanguageCode ?? ''
		},
		usedLanguageCodes () {
			return this.assignedLanguages
				.filter(({ languageID }) => languageID !== this.assignedLanguage?.languageID)
				.map(({ languageCode }) => languageCode)
		},
		readOnlyPermissions () {
			return !this.productAreaPermission(LANGUAGES_MODULE_FULL_PERMISSIONS)
		},
		userFullPermissions () {
			return this.productAreaPermission(LANGUAGES_MODULE_FULL_PERMISSIONS)
		},
		optionLabelTranslationsTableHeaders () {
			const translationTableHeaders = [
				{ value: 'languageName', text: 'Language', width: '15%' },
				{ value: 'isTranslated', text: 'Translated Status', width: '15%' },
				{ value: 'optInTranslation', text: 'Opt In Label', width: '15%' },
				{ value: 'optOutTranslation', text: 'Opt Out Label', width: '15%' },
				{ value: 'noChangeTranslation', text: 'No Change Label', width: '15%', sortable: false }
			]
			// Filter out the Action column if user doesn't have full permissions as it will be empty and look weird
			if (this.userFullPermissions) {
				translationTableHeaders.push({ value: 'action', text: 'Action', width: '10%' })
			}
			return translationTableHeaders
		},
		multipleLanguagesEnabled () {
			return this.languages.length > 0
		},
		filteredOptionLabelTranslations () {
			const defaultLanguage = this.lookupData.find(({ languageID }) => languageID === 0)
			return [
				defaultLanguage,
				...this.lookupData.filter(({ languageID }) => languageID !== 0).sort((a, b) => a.languageName.localeCompare(b.languageName))
			]
		}
	},
	async created () {
		await this.loadLanguages()
		const firstLanguage = this.assignedLanguages[0]
		if (firstLanguage) {
			this.defaultLanguage = firstLanguage.languageName.replace(' (Default)', '')
		}
		this.loadOptionLables()
		const { data: assignedLanguages } = await getAssignedLanguages()
		this.languages = assignedLanguages
	},
	methods: {
		async loadOptionLables () {
			const { data: lookupLanguageData } = await getLanguageTabTranslations()
			this.lookupData = lookupLanguageData
		},
		editAssignedLanguage ({ enabledInPublicPortal, allowPrefPageAutoTranslation, languageLookupID, languageName, languageCode, languageID, brandID }) {
			this.isEdit = true
			this.assignedLanguage = {
				language: {
					languageLookupId: languageLookupID,
					languageName: languageName,
					googleLanguageCode: languageCode,
					languageCode: languageCode,
					brandID: brandID
				},
				enabledInPublicPortal: Boolean(enabledInPublicPortal),
				allowPrefPageAutoTranslation: Boolean(allowPrefPageAutoTranslation),
				languageID: languageID,
				languageName: languageName,
				languageCode: languageCode
			}
		},
		async loadLanguages () {
			const { data: assignedLanguages } = await getAssignedLanguages()
			const { data: languages } = await getLanguages()
			this.assignedLanguages = assignedLanguages
			this.languages = languages
			const DEFAULT_LANGUAGE = 0
			this.defaultLanguage = this.assignedLanguages.find(({ languageID }) => languageID === DEFAULT_LANGUAGE).languageName
		},
		addAssignedLanguage () {
			this.showAddModal = true
			this.isEdit = false
			this.assignedLanguage = {
				languageLookupId: null,
				enabledInPublicPortal: false,
				allowPrefPageAutoTranslation: false,
				languageName: null,
				languageCode: null
			}
		},
		closeSubmitModal () {
			this.showAddModal = false
			this.assignedLanguage = null
		},
		tableData () {
			return this.assignedLanguages.filter(({ languageName, languageCode }) => {
				let check = true
				if (this.searchQuery) check = languageName.toLowerCase().includes(this.searchQuery.toLowerCase())
				if (this.languageCodeSearchQuery) check = languageCode?.toLowerCase().includes(this.languageCodeSearchQuery.toLowerCase())
				return check
			})
		},
		async submit () {
			const {
				allowPrefPageAutoTranslation, enabledInPublicPortal,
				languageCode, languageLookupId, languageID, languageName
			} = this.assignedLanguage

			if (this.usedLanguageCodes.includes(languageCode)) {
				showSnackbar({ text: `Language code '${languageCode} already exists.`, color: 'error' })
				return
			}
			if (this.isEdit) {
				await updateLanguage(languageID,
					languageName.replace(' (Default)', ''),
					languageCode,
					enabledInPublicPortal,
					allowPrefPageAutoTranslation)
				if (languageID === 0) {
					// Reload Default Language
					this.defaultLanguage = languageName
				}
			} else {
				await createLanguage(languageLookupId,
					languageName,
					languageCode,
					enabledInPublicPortal,
					allowPrefPageAutoTranslation)
			}
			showSnackbar(`${languageName} has been ${this.showAddModal ? 'created' : 'updated'}`)
			this.closeSubmitModal()
			this.loadLanguages()
		},
		async SubmitDefaultLanguageName () {
			await updateDefaultLanguageName(this.defaultLanguage.replace(' (Default)', ''))
			showSnackbar(`The Default Language is set to '${this.defaultLanguage}'`)
			this.loadLanguages()
		},
		clearFilters () {
			this.languageCodeSearchQuery = ''
		}
	}
}
</script>
